import { z } from "zod"

import { iddArtSchema, type IddArt } from "./idd"

/**
 * Picked from images the app ships, never a free URL: the page renders it for
 * every advisor, and a typed address would load from wherever it points.
 */
export const IDD_BILDER = [
  "/personas/kacheln/christina.jpg",
  "/personas/kacheln/tobias.jpg",
  "/personas/kacheln/anke.jpg",
  "/personas/kacheln/heiko.jpg",
  "/personas/kacheln/lena.jpg",
  "/personas/kacheln/mia.jpg",
  "/personas/kacheln/murat.jpg",
  "/personas/kacheln/sandra.jpg",
  "/personas/kacheln/tim.jpg",
  "/mediathek/produkt-aktivplan.jpg",
  "/mediathek/produkt-basisrente.jpg",
  "/mediathek/produkt-berufsunfaehigkeit.jpg",
  "/mediathek/filiale.jpg",
  "/mediathek/coach-gespraech-weit.jpg",
  "/menschen/aktivplan.jpg",
  "/menschen/basisrente.jpg",
  "/menschen/einkommensabsicherung.jpg",
  "/menschen/einkommensgutachten.jpg",
] as const

const zeilen = z.array(z.string().trim().min(1).max(300)).max(12)

export const iddModulInhaltEingabeSchema = z.object({
  beschreibung: z.string().trim().min(1).max(1200),
  lernziele: zeilen,
  lerninhalte: zeilen,
  kompetenzen: zeilen,
  zielgruppe: z.string().trim().max(400),
  bild: z.enum(IDD_BILDER),
})
export type IddModulInhaltEingabe = z.infer<typeof iddModulInhaltEingabeSchema>

export const iddModulInhaltSchema = iddModulInhaltEingabeSchema.extend({
  art: iddArtSchema,
  modulKey: z.string(),
  /** False while the module shows the defaults from the code. */
  angepasst: z.boolean(),
  geaendertAm: z.string().optional(),
})
export type IddModulInhalt = z.infer<typeof iddModulInhaltSchema>
export const iddModulInhaltListSchema = z.array(iddModulInhaltSchema)

const ZIELGRUPPE =
  "Alle Vertriebsmitarbeiter und Innendienstmitarbeiter der Sparkasse und neuen leben Versicherungen"

/** What a module shows until the platform edits it. */
export const IDD_INHALT_STANDARD: Record<IddArt, Record<string, IddModulInhaltEingabe>> = {
  gespraech: {
    terminierung: {
      beschreibung:
        "Realistisches Telefonat mit einer Kundin aus dem Bestand. Du stellst dich vor, nennst den Anlass, machst den Nutzen greifbar und vereinbarst einen konkreten Beratungstermin, mit direktem Feedback von deinem Coach.",
      lernziele: [
        "Einen Anruf souverän und verbindlich eröffnen",
        "Den Anlass und den Nutzen eines Termins verständlich machen",
        "Einwände am Telefon aufnehmen, statt sie zu übergehen",
        "Einen konkreten Termin mit Tag und Uhrzeit vereinbaren",
      ],
      lerninhalte: [
        "Gesprächseröffnung am Telefon",
        "Anlass und Nutzen des Einkommensgutachtens",
        "Einwandbehandlung am Telefon",
        "Terminvereinbarung und Unterlagen",
      ],
      kompetenzen: [
        "Gesprächskompetenz am Telefon",
        "Kundenorientierte Argumentation",
        "Sicherer Umgang mit Einwänden",
        "Zielgerichtete Kommunikation",
      ],
      zielgruppe: ZIELGRUPPE,
      bild: "/personas/kacheln/christina.jpg",
    },
    rahmen_bedarf: {
      beschreibung:
        "Realistisches Kundengespräch zum Thema private Altersvorsorge. Über den Gesprächseinstieg, die Bedarfsermittlung und die zielgruppengerechte Ansprache, mit direktem Feedback von deinem Coach.",
      lernziele: [
        "Die Ausgangslage der privaten Altersvorsorge erläutern",
        "Bedarf und Ziele des Kunden ermitteln",
        "Die Versorgungslücke gemeinsam mit dem Kunden herleiten",
        "Risikobereitschaft und nächsten Schritt klären",
      ],
      lerninhalte: [
        "Gesprächsstruktur und -phasen",
        "Bedarfsermittlung und Situationsanalyse",
        "Versorgungslücke mit dem AV-Tool",
        "Praxisnahe Gesprächssimulation mit Feedback",
      ],
      kompetenzen: [
        "Gesprächskompetenz stärken",
        "Kundenbedarf systematisch ermitteln",
        "Sicherer Umgang mit Einwänden",
        "Zielgerichtete Kommunikation",
      ],
      zielgruppe: ZIELGRUPPE,
      bild: "/personas/kacheln/tobias.jpg",
    },
    loesung_abschluss: {
      beschreibung:
        "Folgetermin mit bekanntem Bedarf. Du führst Meilenstein für Meilenstein durch Lösung, Kosten, Einwand und Abschlussfrage, mit direktem Feedback von deinem Coach.",
      lernziele: [
        "Den Bedarf des Kunden zusammenfassen",
        "Eine passende Lösung am Bedarf ausrichten",
        "Kosten und Risiken transparent machen",
        "Den Abschluss souverän herbeiführen",
      ],
      lerninhalte: [
        "Meilensteinbasierte Gesprächsführung",
        "Lösungspräsentation und Kostentransparenz",
        "Einwandbehandlung im Abschlussgespräch",
        "Abschlussfrage und Nachbetreuung",
      ],
      kompetenzen: [
        "Lösungsorientierte Beratung",
        "Transparente Kommunikation",
        "Sicherer Umgang mit Einwänden",
        "Abschlusssicherheit",
      ],
      zielgruppe: ZIELGRUPPE,
      bild: "/personas/kacheln/anke.jpg",
    },
  },
  produkt: {
    aktivplan: {
      beschreibung:
        "Die private Rentenversicherung im Zielsortiment, in sechs Lernbausteinen. Du wählst mindestens drei aus, dein Coach führt dich durch und am Ende steht eine kurze Lernerfolgskontrolle.",
      lernziele: [
        "Zielmarkt und Einsatzgrenzen des Aktivplans kennen",
        "Garantien und Leistungskomponenten sicher erklären",
        "Auszahlungsoptionen und Flexibilität beraten",
      ],
      lerninhalte: [
        "Zielmarkt und Garantien",
        "Leistungskomponenten der Hybridrente",
        "Auszahlungsoptionen und Pflegeleistungen",
        "Zuzahlungen und Entnahmen",
      ],
      kompetenzen: [
        "Produktkenntnis Altersvorsorge",
        "Bedarfsgerechte Produktempfehlung",
        "Verständliche Erklärung komplexer Produkte",
      ],
      zielgruppe: ZIELGRUPPE,
      bild: "/mediathek/produkt-aktivplan.jpg",
    },
  },
}

export function iddInhaltStandard(art: IddArt, modulKey: string): IddModulInhaltEingabe | undefined {
  return IDD_INHALT_STANDARD[art][modulKey]
}
